import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import { api, formatApiErrorDetail } from "../lib/api";
import { ShieldCheck, AlertTriangle } from "lucide-react";

/**
 * Token-redemption page. Reached from the password-reset email link.
 *
 * Captures the `token` query param, posts it alongside the new password to
 * /auth/reset-password, then redirects to /login (preserved across both
 * client + panelist portals because they share a single login page).
 *
 * If the token is missing entirely we render a "link looks broken" CTA
 * instead of letting the user type a password that can never be submitted.
 */
export default function ResetPassword() {
  const [search] = useSearchParams();
  const nav = useNavigate();
  const token = search.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) {
      // Brief celebration moment, then bounce to /login.
      const t = setTimeout(() => nav("/login", { replace: true }), 2400);
      return () => clearTimeout(t);
    }
  }, [done, nav]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="reset-password-page">
        <Navigation />
        <div className="max-w-md mx-auto px-6 py-16" data-testid="reset-password-missing-token">
          <AlertTriangle className="w-10 h-10 text-[var(--rl-accent)]" />
          <h1 className="font-display font-black text-3xl tracking-tight mt-5">This link looks broken.</h1>
          <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">
            We couldn't find a reset token in this URL. Reset links expire after 1 hour — request a new one and try again.
          </p>
          <Link to="/forgot-password" className="inline-block mt-6 bg-[var(--rl-text)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors" data-testid="reset-password-request-new">Request a new link</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pwd.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pwd !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="reset-password-page">
      <Navigation />
      <div className="max-w-md mx-auto px-6 py-16">
        {done ? (
          <div data-testid="reset-password-success">
            <ShieldCheck className="w-12 h-12 text-[var(--rl-success)]" />
            <h1 className="font-display font-black text-3xl tracking-tight mt-5">Password updated.</h1>
            <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">
              You've been signed out of every device. Redirecting you to sign in…
            </p>
          </div>
        ) : (
          <>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Account recovery</div>
            <h1 className="font-display font-black text-4xl tracking-tight mt-2">Choose a new password.</h1>
            <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">
              At least 8 characters. We recommend a passphrase — three random words is stronger than mixed symbols.
            </p>
            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="reset-password-form">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-2">New password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)] transition-colors"
                  data-testid="reset-password-new-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-2">Confirm new password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)] transition-colors"
                  data-testid="reset-password-confirm-input"
                />
              </div>
              {error && <div className="text-sm text-[var(--rl-accent)] font-medium" data-testid="reset-password-error">{error}</div>}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[var(--rl-text)] text-white py-4 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50"
                data-testid="reset-password-submit-btn"
              >
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
